import React from 'react'
import { Controller, FieldError, FieldErrors, UseControllerProps, useFormContext } from 'react-hook-form'
import i18n from '../../i18n'

export type FormFieldProps<T> = UseControllerProps & T

const FormField = <T extends React.ElementType>({
  name,
  Component,
  onChange: customOnChange,
  rules,
  readOnly,
  ...rest
}: FormFieldProps<React.ComponentProps<T>> & {
  Component: T
}) => {
  const { control, watch } = useFormContext()

  if (readOnly) return <Component readOnly={readOnly} value={watch(name)} {...rest} />

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, onBlur, value, ref } }) => (
        <Component
          {...rest}
          onChange={(value: unknown) => {
            if (customOnChange) customOnChange(value)
            onChange(value)
          }}
          onBlur={onBlur}
          value={value}
          inputRef={ref}
          required={rules?.required}
        />
      )}
    />
  )
}

const manageFormError = (error: FieldError, messageHandler: (s: string, t: string) => void) => {
  if (error?.type) {
    if (error?.type === 'required') {
      messageHandler('warning', i18n.t('input_compulsory_fields_missing'))
    } else if (error) {
      messageHandler('warning', i18n.t('input_fields_in_error'))
    }
    return
  } else if (Object.values(error)[0]?.type) {
    manageFormError(Object.values(error)[0], messageHandler)
  }
}

const manageFormErrors = (errors: FieldErrors, messageHandler: (s: string, t: string) => void) => {
  Object.values(errors).forEach((error) => error && manageFormError(error, messageHandler))
}

export { manageFormErrors }

export default FormField
